/**
 * ErvaPOS - Monthly Timesheets Page
 * Controlo de horas de trabalho dos funcionários
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Plus,
  Loader2,
  Clock,
  Calendar,
  FileText,
  Download,
  CheckCircle,
  Edit,
  User,
  Save
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const timesheetsAPI = {
  getAll: (params = {}) => api.get('/monthly-timesheets', { params }),
  getById: (id) => api.get(`/monthly-timesheets/${id}`),
  create: (data) => api.post('/monthly-timesheets', data),
  update: (id, data) => api.put(`/monthly-timesheets/${id}`, data),
  approve: (id) => api.put(`/monthly-timesheets/${id}/approve`),
  getReportPDF: (id) => api.get(`/monthly-timesheets/${id}/report-pdf`, { responseType: 'blob' }),
};

const MONTHS_PT = [
  '', 'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

const ABSENCE_TYPES = [
  { value: 'ferias', label: 'Férias' },
  { value: 'doenca', label: 'Doença' },
  { value: 'falta_just', label: 'Falta Justificada' },
  { value: 'falta_injust', label: 'Falta Injustificada' },
  { value: 'licenca', label: 'Licença' },
  { value: 'formacao', label: 'Formação' },
  { value: 'feriado', label: 'Feriado' },
  { value: 'ponte', label: 'Ponte' },
];


const DEFAULT_START = '09:30';
const DEFAULT_END = '19:00';
const DEFAULT_BREAK_MINUTES = 90;

const pad2 = (n) => String(n).padStart(2, '0');

const getEasterSunday = (year) => {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day, 12, 0, 0);
};

const addDaysLocal = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

const sameYmdLocal = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const isWeekdayLocal = (d) => {
  const day = d.getDay();
  return day >= 1 && day <= 5;
};

const isCompanyHoliday = (dateObj) => {
  const year = dateObj.getFullYear();
  const easter = getEasterSunday(year);
  const goodFriday = addDaysLocal(easter, -2);
  const easterMonday = addDaysLocal(easter, 1);
  const carnival = addDaysLocal(easter, -47);

  const fixed = [
    '01-01',
    '04-25',
    '05-01',
    '06-10',
    '06-13',
    '08-15',
    '10-05',
    '11-01',
    '12-01',
    '12-08',
    '12-24',
    '12-25',
    '12-31',
  ];

  const md = `${pad2(dateObj.getMonth() + 1)}-${pad2(dateObj.getDate())}`;
  if (fixed.includes(md)) return true;
  if (sameYmdLocal(dateObj, goodFriday)) return true;
  if (sameYmdLocal(dateObj, easterMonday)) return true;
  if (sameYmdLocal(dateObj, carnival)) return true;

  return false;
};

const shouldPrefillDayLocal = (year, month, day) => {
  const today = new Date();
  const y = today.getFullYear();
  const m = today.getMonth() + 1;
  const d = today.getDate();

  if (year < y) return true;
  if (year > y) return false;
  if (month < m) return true;
  if (month > m) return false;
  return day <= d;
};


const todayYmdLocal = () => {
  const now = new Date();
  return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
};

export const TimesheetsPage = () => {
  const { isManager, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [timesheets, setTimesheets] = useState([]);
  const [employees, setEmployees] = useState([]);
  
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedTimesheet, setSelectedTimesheet] = useState(null);
  const [saving, setSaving] = useState(false);
  const [timesheetsBackendReady, setTimesheetsBackendReady] = useState(false);

  const [createForm, setCreateForm] = useState({
    employee_id: '',
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
  });

  const [editEntries, setEditEntries] = useState([]);
  const [clockSaving, setClockSaving] = useState(false);

  const currentEmployeeId = user?.employee_id || '';

  useEffect(() => {
    if (!currentEmployeeId) return;

    setSelectedEmployee((prev) => {
      if (!prev || prev === 'all') return currentEmployeeId;
      return prev;
    });
  }, [currentEmployeeId]);




  const hoursToHhMm = (hours) => {
    const h = Number(hours || 0);
    const totalMin = Math.round(h * 60);
    const hh = Math.floor(totalMin / 60);
    const mm = totalMin % 60;
    return `${hh}:${String(mm).padStart(2, '0')}`;
  };

  const computeTotals = (entries = []) => {
    let totalWorked = 0;
    let totalAbsence = 0;
    let totalOvertimeMin = 0;

    for (const e of (entries || [])) {
      const st = (e?.start_time || '').trim();
      const en = (e?.end_time || '').trim();
      const br = Number(e?.break_minutes || 0);
      const ot = Number(e?.overtime_minutes || 0);
      const abType = (e?.absence_type || '').trim();
      const abH = Number(e?.absence_hours || 0);

      totalAbsence += abH;
      totalOvertimeMin += ot;

      // se houver ausência com horas, não conta trabalho
      if (abType && abH > 0) continue;

      if (st && en) {
        // calcula em minutos para evitar “0,92” esquisito
        const [sh, sm] = st.split(':').map(Number);
        const [eh, em] = en.split(':').map(Number);
        if (!Number.isFinite(sh) || !Number.isFinite(sm) || !Number.isFinite(eh) || !Number.isFinite(em)) continue;

        const startMin = sh * 60 + sm;
        const endMin = eh * 60 + em;
        let diffMin = endMin - startMin;
        if (diffMin < 0) diffMin += 24 * 60; // caso passe da meia-noite

        const workedMin = Math.max(0, diffMin - br);
        totalWorked += workedMin / 60;
      }
    }

    return {
      total_worked_hours: Math.round(totalWorked * 100) / 100,
      total_absence_hours: Math.round(totalAbsence * 100) / 100,
      total_overtime_hours: Math.round((totalOvertimeMin / 60) * 100) / 100,
    };
  };

  const todayYmd = (() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  })();

  const currentClockEmployeeId =
    selectedEmployee && selectedEmployee !== 'all'
      ? selectedEmployee
      : null;

  const currentMonthTimesheet = useMemo(() => {
    if (!currentClockEmployeeId) return null;

    return (timesheets || []).find(ts =>
      String(ts?.employee_id || '') === String(currentClockEmployeeId) &&
      Number(ts?.year || 0) === Number(selectedYear) &&
      Number(ts?.month || 0) === Number(selectedMonth)
    ) || null;
  }, [timesheets, currentClockEmployeeId, selectedYear, selectedMonth]);

  const todayClockEntry = useMemo(() => {
    return (currentMonthTimesheet?.entries || []).find(e => e?.date === todayYmd) || null;
  }, [currentMonthTimesheet, todayYmd]);

  const clockDayStatus = useMemo(() => {
    if (selectedEmployee === 'all') {
      return {
        tone: 'neutral',
        title: 'Selecione um funcionário',
        subtitle: 'Escolha um funcionário para ver o estado do dia e registar ponto com contexto.'
      };
    }

    if (!todayClockEntry) {
      return {
        tone: 'warning',
        title: 'Dia ainda sem registo',
        subtitle: 'Ainda não existe registo para hoje.'
      };
    }

    const absenceType = String(todayClockEntry?.absence_type || '').trim();
    const absenceHours = Number(todayClockEntry?.absence_hours || 0);
    const startTime = String(todayClockEntry?.start_time || '').trim();
    const endTime = String(todayClockEntry?.end_time || '').trim();

    if (absenceType && absenceHours >= 8) {
      const labels = {
        feriado: 'Feriado',
        ponte: 'Ponte',
        ferias: 'Férias',
        doenca: 'Doença',
        falta_just: 'Falta justificada',
        falta_injust: 'Falta injustificada',
        licenca: 'Licença',
        formacao: 'Formação',
      };

      return {
        tone: 'neutral',
        title: labels[absenceType] || 'Ausência registada',
        subtitle: 'Hoje está marcado como ausência total.'
      };
    }

    if (!startTime) {
      return {
        tone: 'warning',
        title: 'Entrada por registar',
        subtitle: 'Ainda não foi registada a entrada de hoje.'
      };
    }

    if (startTime && !endTime) {
      return {
        tone: 'warning',
        title: 'Saída por confirmar',
        subtitle: `Entrada registada às ${startTime}. Falta registar a saída.`
      };
    }

    return {
      tone: 'success',
      title: 'Dia já registado',
      subtitle: `Entrada ${startTime} · Saída ${endTime}`
    };
  }, [selectedEmployee, todayClockEntry]);

  const clockStatusClasses = useMemo(() => {
    if (clockDayStatus.tone === 'success') {
      return {
        box: 'border-emerald-500/30 bg-emerald-500/10',
        title: 'text-emerald-300',
        subtitle: 'text-emerald-100/80',
      };
    }

    if (clockDayStatus.tone === 'warning') {
      return {
        box: 'border-amber-500/30 bg-amber-500/10',
        title: 'text-amber-300',
        subtitle: 'text-amber-100/80',
      };
    }

    return {
      box: 'border-slate-700 bg-slate-800/40',
      title: 'text-slate-200',
      subtitle: 'text-slate-400',
    };
  }, [clockDayStatus]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const params = { year: selectedYear };
      if (selectedMonth !== 0) params.month = selectedMonth;
      if (selectedEmployee !== 'all') params.employee_id = selectedEmployee;
      
      const [tsRes, empRes] = await Promise.allSettled([
        timesheetsAPI.getAll(params),
        api.get('/employees'),
      ]);

      if (tsRes.status === 'fulfilled') {
        setTimesheetsBackendReady(true);
        const raw = tsRes.value?.data || [];
        const withTotals = raw.map((ts) => {
          const entries = ts?.entries || [];
          const t = computeTotals(entries);
          return { ...ts, ...t };
        });
        setTimesheets(withTotals);
      } else {
        console.error('TIMESHEETS getAll error:', tsRes.reason);
        setTimesheetsBackendReady(false);
        setTimesheets([]);
      }

      if (empRes.status === 'fulfilled') {
        const employeeRows = Array.isArray(empRes.value?.data) ? empRes.value.data : [];
        setEmployees(employeeRows.filter((e) => e?.id && e?.is_active !== false));
      } else {
        console.error('EMPLOYEES getAll error:', empRes.reason);
        setEmployees([]);
      }

      if (tsRes.status === 'rejected') {
        toast.error('Módulo de folhas de ponto ainda sem backend ativo');
      }
    } catch (error) {
      console.error('TIMESHEETS loadData error:', error);
      console.error('TIMESHEETS response:', error?.response?.status, error?.response?.data);
      toast.error(error?.response?.data?.detail || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [selectedYear, selectedMonth, selectedEmployee]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const getDaysInMonth = (year, month) => {
    return new Date(year, month, 0).getDate();
  };

  const generateEmptyEntries = (year, month) => {
    const days = getDaysInMonth(year, month);
    const entries = [];

    for (let day = 1; day <= days; day++) {
      const date = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const dateObj = new Date(`${date}T12:00:00`);
      const isWeekend = !isWeekdayLocal(dateObj);
      const isHoliday = isCompanyHoliday(dateObj);
      const shouldPrefill = !isWeekend && !isHoliday && shouldPrefillDayLocal(year, month, day);

      entries.push({
        date,
        start_time: shouldPrefill ? DEFAULT_START : '',
        end_time: shouldPrefill ? DEFAULT_END : '',
        break_minutes: DEFAULT_BREAK_MINUTES,
        overtime_minutes: 0,
        absence_type: isHoliday ? 'feriado' : '',
        absence_hours: isHoliday ? 8 : 0,
        notes: '',
        absence_override: false,
        start_confirmed: false,
        end_confirmed: false,
      });
    }

    return entries;
  };


  const getLocalDateYmd = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  };

  const getLocalTimeHm = () => {
    const now = new Date();
    return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  };

  const createBlankDayEntry = (date) => ({
    date,
    start_time: '',
    end_time: '',
    break_minutes: DEFAULT_BREAK_MINUTES,
    overtime_minutes: 0,
    absence_type: '',
    absence_hours: 0,
    notes: '',
    absence_override: false,
    start_confirmed: false,
    end_confirmed: false,
  });

  const ensureMyCurrentTimesheet = async () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;

    const existing = await timesheetsAPI.getAll({
      year,
      month,
      employee_id: currentEmployeeId,
    });

    const rows = Array.isArray(existing?.data) ? existing.data : [];
    if (rows.length > 0) return rows[0];

    const entries = generateEmptyEntries(year, month);
    const created = await timesheetsAPI.create({
      employee_id: currentEmployeeId,
      year,
      month,
      entries,
    });

    return created?.data;
  };

  const handleClockAction = async (mode) => {
    if (!currentEmployeeId) {
      toast.error('Este utilizador não tem funcionário associado');
      return;
    }

    const today = getLocalDateYmd();
    const nowHm = getLocalTimeHm();
    const todayObj = new Date(`${today}T12:00:00`);

    if (!isWeekdayLocal(todayObj) || isCompanyHoliday(todayObj)) {
      toast.error('Hoje está marcado como dia não útil/feriado');
      return;
    }

    setClockSaving(true);
    try {
      const ts = await ensureMyCurrentTimesheet();

      if (!ts?.id) {
        throw new Error('Folha mensal sem identificador');
      }

      const entries = Array.isArray(ts.entries) ? [...ts.entries] : [];
      const idx = entries.findIndex((e) => e?.date === today);

      const current = idx >= 0 ? { ...entries[idx] } : createBlankDayEntry(today);

      if (mode === 'in') {
        current.start_time = nowHm;
        current.start_confirmed = true;

        if (['feriado', 'ponte'].includes(String(current.absence_type || '').trim())) {
          current.absence_type = '';
          current.absence_hours = 0;
        }
      }

      if (mode === 'out') {
        if (!String(current.start_time || '').trim()) {
          current.start_time = DEFAULT_START;
          current.start_confirmed = true;
        }

        current.end_time = nowHm;
        current.end_confirmed = true;

        if (['feriado', 'ponte'].includes(String(current.absence_type || '').trim())) {
          current.absence_type = '';
          current.absence_hours = 0;
        }
      }

      if (!Number.isFinite(Number(current.break_minutes))) {
        current.break_minutes = DEFAULT_BREAK_MINUTES;
      }

      if (idx >= 0) {
        entries[idx] = current;
      } else {
        entries.push(current);
      }

      await timesheetsAPI.update(ts.id, {
        employee_id: ts.employee_id,
        year: ts.year,
        month: ts.month,
        entries,
        notes: ts.notes || '',
      });

      toast.success(mode === 'in' ? 'Entrada registada com sucesso' : 'Saída registada com sucesso');
      await loadData();
    } catch (error) {
      console.error('CLOCK ACTION ERROR:', error);
      console.error('CLOCK ACTION STATUS:', error?.response?.status);
      console.error('CLOCK ACTION DATA:', error?.response?.data);
      toast.error(error?.response?.data?.detail || 'Erro ao registar ponto');
    } finally {
      setClockSaving(false);
    }
  };

  const createTimesheet = async () => {
    if (!createForm.employee_id) {
      toast.error('Selecione um funcionário');
      return;
    }
    setSaving(true);
    try {
      const entries = generateEmptyEntries(createForm.year, createForm.month);
      const response = await timesheetsAPI.create({
        ...createForm,
        entries,
      });
      toast.success('Folha de ponto criada');
      setShowCreateModal(false);
      setCreateForm({ employee_id: '', year: new Date().getFullYear(), month: new Date().getMonth() + 1 });
      loadData();
      
      // Open for editing
      openEditModal(response.data);
    } catch (error) {
      console.error('CREATE TIMESHEET ERROR:', error);
      console.error('CREATE TIMESHEET STATUS:', error?.response?.status);
      console.error('CREATE TIMESHEET DATA:', error?.response?.data);
      toast.error(error?.response?.data?.detail || 'Erro ao criar folha de ponto');
    } finally {
      setSaving(false);
    }
  };

  const openEditModal = (timesheet) => {
    setSelectedTimesheet(timesheet);
    setEditEntries(timesheet.entries || []);
    setShowEditModal(true);
  };

    const handleEditClick = async (ts) => {
      if (!ts) return;
      let tsToEdit = ts;

      // Se estiver aprovado, perguntar o que fazer
      if (ts?.status === 'approved') {
        const keepApproved = window.confirm(
          "Esta folha está APROVADA.\n\nOK: editar e manter APROVADA\nCancelar: voltar a RASCUNHO e editar"
        );

        // Cancelar => voltar a draft antes de editar
        if (!keepApproved) {
          try {
            await timesheetsAPI.update(ts.id, { status: 'draft' });
            toast.success('Folha passou a rascunho');
            tsToEdit = { ...ts, status: 'draft' };
            // refrescar lista (opcional, mas ajuda a UI)
            try { await loadData(); } catch (_) {}
          } catch (e) {
            toast.error(e?.response?.data?.detail || 'Erro ao mudar para rascunho');
            return;
          }
        }
      }

      openEditModal(tsToEdit);
    };

  const updateEntry = (index, field, value) => {
    setEditEntries((prev) => {
      const entries = [...(prev || [])];
      entries[index] = { ...(entries[index] || {}), [field]: value };
      return entries;
    });
  };

  const saveTimesheet = async () => {
    if (!selectedTimesheet) return;
    setSaving(true);
    try {
      await timesheetsAPI.update(selectedTimesheet.id, {
        employee_id: selectedTimesheet.employee_id,
        year: selectedTimesheet.year,
        month: selectedTimesheet.month,
        entries: editEntries,
        notes: selectedTimesheet.notes,
      });
      toast.success('Folha de ponto guardada');
      setShowEditModal(false);
      loadData();
    } catch (error) {
      toast.error('Erro ao guardar');
    } finally {
      setSaving(false);
    }
  };

  const approveTimesheet = async (id) => {
    try {
      await timesheetsAPI.approve(id);
      toast.success('Folha de ponto aprovada');
      loadData();
    } catch (error) {
      toast.error('Erro ao aprovar');
    }
  };

  const downloadPDF = async (timesheet) => {
    try {
      const response = await timesheetsAPI.getReportPDF(timesheet.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mapa_horas_${timesheet.employee_name.replace(/\s/g, '_')}_${timesheet.year}_${String(timesheet.month).padStart(2, '0')}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF descarregado');
    } catch (error) {
      toast.error('Erro ao gerar PDF');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'draft':
        return <Badge className="badge-warning">Rascunho</Badge>;
      case 'submitted':
        return <Badge className="badge-info">Submetido</Badge>;
      case 'approved':
        return <Badge className="badge-success">Aprovado</Badge>;
      case 'rejected':
        return <Badge className="bg-rose-500/20 text-rose-400">Rejeitado</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const getDayName = (dateStr) => {
    const days = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
    return days[new Date(dateStr).getDay()];
  };


  const todayClockStatus = useMemo(() => {
    const today = todayYmdLocal();
    const todayDateObj = new Date(`${today}T12:00:00`);

    if (isCompanyHoliday(todayDateObj)) {
      return {
        label: 'Feriado',
        className: 'bg-amber-500/15 text-amber-300 border border-amber-500/30',
      };
    }

    if (!isWeekdayLocal(todayDateObj)) {
      return {
        label: 'Fim de semana',
        className: 'bg-slate-700/40 text-slate-300 border border-slate-600',
      };
    }

    const activeEmployeeId =
      selectedEmployee !== 'all'
        ? selectedEmployee
        : (createForm.employee_id || selectedTimesheet?.employee_id || '');

    const currentTimesheet = (timesheets || []).find((ts) => {
      if (!ts) return false;
      if (Number(ts.year) !== Number(selectedYear)) return false;
      if (Number(ts.month) !== Number(selectedMonth)) return false;
      if (!activeEmployeeId) return true;
      return String(ts.employee_id || '') === String(activeEmployeeId);
    });

    const entry = (currentTimesheet?.entries || []).find((e) => String(e?.date || '') === today);

    if (!entry) {
      return {
        label: 'Entrada por confirmar',
        className: 'bg-rose-500/15 text-rose-300 border border-rose-500/30',
      };
    }

    const isAbsence = String(entry?.absence_type || '').trim() && Number(entry?.absence_hours || 0) > 0;
    if (isAbsence) {
      const absenceLabel = String(entry.absence_type || '').trim();
      const pretty =
        absenceLabel === 'feriado' ? 'Feriado'
        : absenceLabel === 'ponte' ? 'Ponte'
        : absenceLabel === 'ferias' ? 'Férias'
        : absenceLabel === 'doenca' ? 'Doença'
        : absenceLabel === 'falta_just' ? 'Falta justificada'
        : absenceLabel === 'falta_injust' ? 'Falta injustificada'
        : absenceLabel === 'licenca' ? 'Licença'
        : absenceLabel === 'formacao' ? 'Formação'
        : 'Ausência';
      return {
        label: pretty,
        className: 'bg-amber-500/15 text-amber-300 border border-amber-500/30',
      };
    }

    const hasStart = !!String(entry?.start_time || '').trim() || !!entry?.start_confirmed;
    const hasEnd = !!String(entry?.end_time || '').trim() || !!entry?.end_confirmed;

    if (!hasStart) {
      return {
        label: 'Entrada por confirmar',
        className: 'bg-rose-500/15 text-rose-300 border border-rose-500/30',
      };
    }

    if (hasStart && !hasEnd) {
      return {
        label: 'Saída por confirmar',
        className: 'bg-blue-500/15 text-blue-300 border border-blue-500/30',
      };
    }

    return {
      label: 'Dia concluído',
      className: 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30',
    };
  }, [
    timesheets,
    selectedEmployee,
    selectedYear,
    selectedMonth,
    createForm.employee_id,
    selectedTimesheet,
  ]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white font-['Manrope']">Folhas de Ponto</h1>
          <p className="text-slate-400">Controlo de horas de trabalho dos funcionários</p>
        </div>
        <Button
          onClick={() => setShowCreateModal(true)}
          className="bg-emerald-500 hover:bg-emerald-600"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nova Folha
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <Select value={String(selectedYear)} onValueChange={(val) => setSelectedYear(parseInt(val))}>
          <SelectTrigger className="w-32 bg-slate-800 border-slate-700">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {[2024, 2025, 2026, 2027].map(y => (
              <SelectItem key={y} value={String(y)}>{y}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={String(selectedMonth)} onValueChange={(val) => setSelectedMonth(parseInt(val))}>
          <SelectTrigger className="w-40 bg-slate-800 border-slate-700">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="0">Todos os meses</SelectItem>
            {MONTHS_PT.slice(1).map((m, i) => (
              <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <select
          value={selectedEmployee}
          onChange={(e) => setSelectedEmployee(e.target.value)}
          className="w-48 bg-slate-800 border border-slate-700 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500"
        >
          <option value="all">Todos funcionários</option>
          {employees.map((e) => (
            <option key={e.id} value={e.id}>{e.name}</option>
          ))}
        </select>
      </div>

      {currentEmployeeId && (
        <Card className="card-dark">
          <CardContent className="p-5">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold text-white">Relógio de Ponto</h2>

                <div className={`mt-3 rounded-xl border p-3 ${clockStatusClasses.box}`}>
                  <p className={`text-sm font-semibold ${clockStatusClasses.title}`}>
                    {clockDayStatus.title}
                  </p>
                  <p className={`mt-1 text-xs ${clockStatusClasses.subtitle}`}>
                    {clockDayStatus.subtitle}
                  </p>
                </div>
                <p className="mt-2 text-sm text-slate-400">
                  Estado do dia atual para a folha selecionada.
                </p>
                <div className={`mt-3 inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${todayClockStatus.className}`}>
                  {todayClockStatus.label}
                </div>
                <p className="text-sm text-slate-400">
                  Registo rápido do dia de hoje. Horário base: {DEFAULT_START} às {DEFAULT_END}, almoço 13:00 às 14:30.
                </p>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={() => handleClockAction('in')}
                  disabled={clockSaving}
                  className="bg-emerald-500 hover:bg-emerald-600"
                >
                  {clockSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Clock className="w-4 h-4 mr-2" />}
                  Dar entrada
                </Button>

                <Button
                  onClick={() => handleClockAction('out')}
                  disabled={clockSaving}
                  variant="outline"
                  className="border-slate-700"
                >
                  {clockSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Clock className="w-4 h-4 mr-2" />}
                  Dar saída
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Timesheets Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {timesheets.map((ts) => (
          <Card key={ts.id} className="card-dark">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="text-white text-lg">{ts.employee_name}</CardTitle>
                  <p className="text-sm text-slate-400">
                    {MONTHS_PT[ts.month]} {ts.year}
                  </p>
                </div>
                {getStatusBadge(ts.status)}
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="p-2 bg-slate-800 rounded">
                    <p className="text-xs text-slate-400">Trabalhadas</p>
                    <p className="text-lg font-bold text-emerald-400">{hoursToHhMm(ts.total_worked_hours)}h</p>
                  </div>
                  <div className="p-2 bg-slate-800 rounded">
                    <p className="text-xs text-slate-400">Extra</p>
                    <p className="text-lg font-bold text-blue-400">{hoursToHhMm(ts.total_overtime_hours)}h</p>
                  </div>
                  <div className="p-2 bg-slate-800 rounded">
                    <p className="text-xs text-slate-400">Ausências</p>
                    <p className="text-lg font-bold text-amber-400">{hoursToHhMm(ts.total_absence_hours)}h</p>
                  </div>
                </div>

                <div className="flex gap-2 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEditClick(ts)}
                    className="flex-1 border-slate-700"
                  >
                    <Edit className="w-3 h-3 mr-1" />
                    Editar
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => downloadPDF(ts)}
                    className="flex-1 border-slate-700"
                  >
                    <Download className="w-3 h-3 mr-1" />
                    PDF
                  </Button>
                  {isManager && ts.status !== 'approved' && (
                    <Button
                      size="sm"
                      onClick={() => approveTimesheet(ts.id)}
                      className="bg-emerald-500 hover:bg-emerald-600"
                    >
                      <CheckCircle className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {timesheets.length === 0 && (
          <Card className="card-dark col-span-full p-8 text-center">
            <Clock className="w-12 h-12 mx-auto mb-3 text-slate-500 opacity-50" />
            <p className="text-slate-500">Nenhuma folha de ponto encontrada</p>
            <Button
              onClick={() => setShowCreateModal(true)}
              className="mt-4 bg-emerald-500 hover:bg-emerald-600"
            >
              <Plus className="w-4 h-4 mr-2" />
              Criar Nova
            </Button>
          </Card>
        )}
      </div>

      {/* Create Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Nova Folha de Ponto</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label className="text-slate-300">Funcionário</Label>
              <select
                value={createForm.employee_id || ""}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, employee_id: e.target.value }))}
                className="w-full bg-slate-800 border border-slate-700 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500"
              >
                <option value="">Selecionar...</option>
                {employees.map((e) => (
                  <option key={e.id} value={e.id}>{e.name}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Ano</Label>
                <Select
                  value={String(createForm.year)}
                  onValueChange={(val) => setCreateForm(prev => ({ ...prev, year: parseInt(val) }))}
                >
                  <SelectTrigger className="bg-slate-800 border-slate-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[2024, 2025, 2026, 2027].map(y => (
                      <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300">Mês</Label>
                <Select
                  value={String(createForm.month)}
                  onValueChange={(val) => setCreateForm(prev => ({ ...prev, month: parseInt(val) }))}
                >
                  <SelectTrigger className="bg-slate-800 border-slate-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTHS_PT.slice(1).map((m, i) => (
                      <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)} className="border-slate-700">
              Cancelar
            </Button>
            <Button
              onClick={createTimesheet}
              disabled={saving || !timesheetsBackendReady}
              className="bg-emerald-500 hover:bg-emerald-600"
              title={timesheetsBackendReady ? 'Criar folha' : 'Backend das folhas de ponto ainda não está disponível'}
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Criar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="bg-slate-900 border-slate-800 w-[98vw] max-w-[1600px] max-h-[92vh]">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Clock className="w-5 h-5 text-emerald-400" />
              {selectedTimesheet?.employee_name} - {MONTHS_PT[selectedTimesheet?.month]} {selectedTimesheet?.year}
            </DialogTitle>
          </DialogHeader>
          
          <ScrollArea className="h-[68vh] pr-4">
            <Table className="table-dark">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-20">Dia</TableHead>
                  <TableHead className="w-24">Entrada</TableHead>
                  <TableHead className="w-24">Saída</TableHead>
                  <TableHead className="w-24">Intervalo</TableHead>
                  <TableHead className="w-24">H. Extra</TableHead>
                  <TableHead className="w-36">Ausência</TableHead>
                        <TableHead className="text-slate-300">Horas ausência</TableHead>
                  <TableHead className="min-w-[320px]">Notas</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {editEntries.map((entry, idx) => {
                  const dayOfWeek = new Date(entry.date).getDay();
                  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                  
                  return (
                    <TableRow key={idx} className={isWeekend ? 'bg-slate-800/50' : ''}>
                      <TableCell>
                        <div>
                          <span className="font-medium text-white">{entry.date.split('-')[2]}</span>
                          <span className={`text-xs ml-1 ${isWeekend ? 'text-amber-400' : 'text-slate-500'}`}>
                            {getDayName(entry.date)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Input
                          type="time"
                          value={entry.start_time || ''}
                          onChange={(e) => {
                              const v = e.target.value;
                              setEditEntries((prev) => {
                                const entries = [...(prev || [])];
                                const cur = { ...(entries[idx] || {}), start_time: v }
                                  cur.start_confirmed = true;;

                                const abType = String(cur.absence_type || '').trim();
                                const override = !!cur.absence_override;

                                if (abType && !override) {
                                  const hasTimes = String(cur.start_time || '').trim() && String(cur.end_time || '').trim();
                                  if (!hasTimes) {
                                    // se não há horas, mantém ausência total
                                    cur.absence_hours = 8;
                                  } else {
                                    const stMin = _timeToMin(cur.start_time);
                                    const enMin = _timeToMin(cur.end_time);
                                    const br = parseInt(cur.break_minutes ?? 0, 10) || 0;
                                    const workedMin = Math.max(0, (enMin ?? 0) - (stMin ?? 0) - br);
                                    const absenceMin = Math.max(0, 480 - workedMin);
                                    cur.absence_hours = Math.round((absenceMin / 60) * 100) / 100;
                                  }
                                }

                                entries[idx] = cur;
                                return entries;
                              });
                            }}
                          className="bg-slate-800 border-slate-700 h-8 text-xs"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="time"
                          value={entry.end_time || ''}
                          onChange={(e) => {
                              const v = e.target.value;
                              setEditEntries((prev) => {
                                const entries = [...(prev || [])];
                                const cur = { ...(entries[idx] || {}), end_time: v }
                                  cur.end_confirmed = true;;

                                const abType = String(cur.absence_type || '').trim();
                                const override = !!cur.absence_override;

                                if (abType && !override) {
                                  const hasTimes = String(cur.start_time || '').trim() && String(cur.end_time || '').trim();
                                  if (!hasTimes) {
                                    // se não há horas, mantém ausência total
                                    cur.absence_hours = 8;
                                  } else {
                                    const stMin = _timeToMin(cur.start_time);
                                    const enMin = _timeToMin(cur.end_time);
                                    const br = parseInt(cur.break_minutes ?? 0, 10) || 0;
                                    const workedMin = Math.max(0, (enMin ?? 0) - (stMin ?? 0) - br);
                                    const absenceMin = Math.max(0, 480 - workedMin);
                                    cur.absence_hours = Math.round((absenceMin / 60) * 100) / 100;
                                  }
                                }

                                entries[idx] = cur;
                                return entries;
                              });
                            }}
                          className="bg-slate-800 border-slate-700 h-8 text-xs"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          min="0"
                          max="120"
                          value={entry.break_minutes}
                          onChange={(e) => {
                              const v = parseInt(e.target.value) || 0;
                              setEditEntries((prev) => {
                                const entries = [...(prev || [])];
                                const cur = { ...(entries[idx] || {}), break_minutes: v };

                                const abType = String(cur.absence_type || '').trim();
                                const override = !!cur.absence_override;

                                if (abType && !override) {
                                  const hasTimes = String(cur.start_time || '').trim() && String(cur.end_time || '').trim();
                                  if (!hasTimes) {
                                    // se não há horas, mantém ausência total
                                    cur.absence_hours = 8;
                                  } else {
                                    const stMin = _timeToMin(cur.start_time);
                                    const enMin = _timeToMin(cur.end_time);
                                    const br = parseInt(cur.break_minutes ?? 0, 10) || 0;
                                    const workedMin = Math.max(0, (enMin ?? 0) - (stMin ?? 0) - br);
                                    const absenceMin = Math.max(0, 480 - workedMin);
                                    cur.absence_hours = Math.round((absenceMin / 60) * 100) / 100;
                                  }
                                }

                                entries[idx] = cur;
                                return entries;
                              });
                            }}
                          className="bg-slate-800 border-slate-700 h-8 text-xs w-16"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          min="0"
                          value={entry.overtime_minutes}
                          onChange={(e) => {
                              const v = parseInt(e.target.value) || 0;
                              setEditEntries((prev) => {
                                const entries = [...(prev || [])];
                                const cur = { ...(entries[idx] || {}), overtime_minutes: v };

                                const abType = String(cur.absence_type || '').trim();
                                const override = !!cur.absence_override;

                                if (abType && !override) {
                                  const hasTimes = String(cur.start_time || '').trim() && String(cur.end_time || '').trim();
                                  if (!hasTimes) {
                                    // se não há horas, mantém ausência total
                                    cur.absence_hours = 8;
                                  } else {
                                    const stMin = _timeToMin(cur.start_time);
                                    const enMin = _timeToMin(cur.end_time);
                                    const br = parseInt(cur.break_minutes ?? 0, 10) || 0;
                                    const workedMin = Math.max(0, (enMin ?? 0) - (stMin ?? 0) - br);
                                    const absenceMin = Math.max(0, 480 - workedMin);
                                    cur.absence_hours = Math.round((absenceMin / 60) * 100) / 100;
                                  }
                                }

                                entries[idx] = cur;
                                return entries;
                              });
                            }}
                          className="bg-slate-800 border-slate-700 h-8 text-xs w-16"
                        />
                      </TableCell>
                      <TableCell>
                        <Select
                          value={entry.absence_type || "none"}
                          onValueChange={(val) => {
                              const isNone = !val || val === "none";

                              setEditEntries((prev) => {
                                const entries = [...(prev || [])];
                                const cur = { ...(entries[idx] || {}) };

                                if (isNone) {
                                  cur.absence_type = '';
                                  cur.absence_hours = 0;
                                  cur.absence_override = false;
                                  entries[idx] = cur;
                                  return entries;
                                }

                                // Define motivo e volta ao modo AUTO por defeito
                                cur.absence_type = val;
                                cur.absence_override = false;

                                  // Por defeito, ao escolher motivo: ausência TOTAL (8h) e limpa o dia.
                                  // Se for parcial, o utilizador preenche as horas reais e o auto recalcula.
                                  cur.start_time = '';
                                  cur.end_time = '';
                                  cur.break_minutes = 0;
                                  cur.overtime_minutes = 0;
                                  cur.absence_hours = 8;

                                entries[idx] = cur;
                                return entries;
                              });
                            }}
                        >
                          <SelectTrigger className="bg-slate-800 border-slate-700 h-8 text-xs">
                            <SelectValue placeholder="-" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">-</SelectItem>
                            {ABSENCE_TYPES.map(t => (
                              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>

                        <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-200 w-10">
                            {hoursToHhMm(Number(entry.absence_hours || 0))}h
                          </span>

                          {!entry.absence_override ? (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="h-7 px-2 border-slate-700 text-xs"
                              onClick={() => {
                                setEditEntries((prev) => {
                                  const entries = [...(prev || [])];
                                  let cur = { ...(entries[idx] || {}) };

                                  cur = applyAbsenceAuto(cur);

                                  cur.absence_override = true;
                                  entries[idx] = cur;
                                  return entries;
                                });
                              }}
                            >
                              Editar
                            </Button>
                          ) : (
                            <>
                              <Input
                                type="number"
                                min="0"
                                max="8"
                                step="0.25"
                                value={Number(entry.absence_hours || 0)}
                                onChange={(e) => {
                                  const h = Number(String(e.target.value || 0).replace(',', '.'));
                                  setEditEntries((prev) => {
                                    const entries = [...(prev || [])];
                                    const cur = { ...(entries[idx] || {}) };
                                    cur.absence_hours = Math.max(0, Math.min(8, h));
                                    cur.absence_override = true;
                                    entries[idx] = cur;
                                    return entries;
                                  });
                                }}
                                className="bg-slate-800 border-slate-700 h-7 text-xs w-20"
                              />
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="h-7 px-2 border-slate-700 text-xs"
                                onClick={() => {
                                  setEditEntries((prev) => {
                                    const entries = [...(prev || [])];
                                    let cur = { ...(entries[idx] || {}) };
                                    cur.absence_override = false;
                                    // voltar ao auto imediatamente
                                    const abType = String(cur.absence_type || '').trim();
                                    if (abType) {
                                      const hasTimes = String(cur.start_time || '').trim() && String(cur.end_time || '').trim();
                                      if (!hasTimes) {
                                        cur.absence_hours = 8;
                                      } else {
                                        const stMin = _timeToMin(cur.start_time);
                                        const enMin = _timeToMin(cur.end_time);
                                        const br = parseInt(cur.break_minutes ?? 0, 10) || 0;
                                        const workedMin = Math.max(0, (enMin ?? 0) - (stMin ?? 0) - br);
                                        const absenceMin = Math.max(0, 480 - workedMin);
                                        cur.absence_hours = Math.round((absenceMin / 60) * 100) / 100;
                                      }
                                    } else {
                                      cur.absence_hours = 0;
                                    }
                                    entries[idx] = cur;
                                    return entries;
                                  });
                                }}
                              >
                                Auto
                              </Button>
                            </>
                          )}
                        </div>
                        </TableCell>
                      <TableCell>
                        <Input
                          value={entry.notes || ''}
                          onChange={(e) => updateEntry(idx, 'notes', e.target.value)}
                          className="min-w-[300px] bg-slate-800 border-slate-700"
                          className="bg-slate-800 border-slate-700 h-8 text-xs"
                          placeholder="..."
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </ScrollArea>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)} className="border-slate-700">
              Cancelar
            </Button>
            <Button onClick={saveTimesheet} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const EXPECTED_HOURS = 8;

const _timeToMin = (t) => {
  if (!t) return null;
  const s = String(t).trim();
  if (!s || s === '--:--') return null;
  const [hh, mm] = s.split(':').map((x) => parseInt(x, 10));
  if (Number.isNaN(hh) || Number.isNaN(mm)) return null;
  return hh * 60 + mm;
};

const calcWorkedMinutes = (entry) => {
  const st = _timeToMin(entry?.start_time);
  const en = _timeToMin(entry?.end_time);
  if (st === null || en === null) return 0;
  let diff = en - st;
  if (diff < 0) diff = 0;
  const br = parseInt(entry?.break_minutes ?? 0, 10) || 0;
  diff = diff - br;
  return Math.max(0, diff);
};

const applyAbsenceAuto = (entry) => {
  const abType = String(entry?.absence_type || '').trim();
  if (!abType) {
    return { ...entry, absence_hours: 0, absence_override: falseOr(entry?.absence_override, false) };
  }
  const workedMin = calcWorkedMinutes(entry);
  const expectedMin = EXPECTED_HOURS * 60;

  // Se não há horas (worked=0 e start/end vazios) => ausência total (8h) e limpar dia
  const hasTimes = String(entry?.start_time || '').trim() && String(entry?.end_time || '').trim();
  if (!hasTimes) {
    return {
      ...entry,
      start_time: '',
      end_time: '',
      break_minutes: 0,
      overtime_minutes: 0,
      absence_hours: EXPECTED_HOURS,
    };
  }

  // Caso parcial: ausência = 8h - horas trabalhadas
  const absenceMin = Math.max(0, expectedMin - workedMin);
  return { ...entry, absence_hours: Math.round((absenceMin / 60) * 100) / 100 };
};

function falseOr(v, fallback) {
  return typeof v === 'boolean' ? v : fallback;
}

export default TimesheetsPage;
